import React, { useState } from 'react';
import { AlertCircle, CalendarClock, Edit3, FileText, Hash, Plus, Search } from 'lucide-react';
import { DataTable, ColumnDef } from '../../../../../components/shared/DataTable/DataTable';
import { MasterDetailLayout } from '../../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction, editAction, deleteAction } from '../../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailField,
  DetailFieldList,
  DetailHeaderButton,
  DetailIdentity,
  DetailNotes,
  DetailSection
} from '../../../../../components/shared/DetailPanel/DetailBlocks';
import { SidebarForm } from '../../../../../components/shared/SidebarForm/SidebarForm';
import Modal from '../../../../../components/shared/Modal/Modal';
import { useClickOutside, isDetailKeepZone } from '../../../../../hooks/useClickOutside';
import { useToast } from '../../../../../hooks/useToast';
import { usePaymentConditions } from '../../../../../hooks/usePaymentConditions';
import {
  PaymentConditionInput,
  createPaymentCondition,
  deletePaymentCondition,
  updatePaymentCondition
} from '../../../../../services/PaymentConditionsService';
import { PaymentCondition } from './financeMasters';
import '../../shared-modules.css';
import './FinanceMasters.css';

type ConditionForm = PaymentConditionInput;

const EMPTY_FORM: ConditionForm = { name: '', days: 30, description: '', isActive: true };

const daysLabel = (days: number) => (days === 0 ? 'Al contado' : `${days} días`);

export const FinancePaymentTermsView = () => {
  const { notify } = useToast();

  const { conditions, loading, error: loadError, reload } = usePaymentConditions();
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PaymentCondition | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedCondition, setSelectedCondition] = useState<PaymentCondition | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<ConditionForm>(EMPTY_FORM);
  const [formError, setFormError] = useState('');

  const setField = <K extends keyof ConditionForm>(field: K, value: ConditionForm[K]) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const handleCreateNewClick = () => {
    setSelectedCondition(null);
    setFormData(EMPTY_FORM);
    setIsEditing(false);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleEditClick = (condition: PaymentCondition) => {
    const { id, dbId, ...rest } = condition;
    setSelectedCondition(condition);
    setFormData({ ...EMPTY_FORM, ...rest });
    setIsEditing(true);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleSelectCondition = (condition: PaymentCondition) => {
    setIsFormOpen(false);
    setSelectedCondition(condition);
  };

  const closePanel = () => {
    setIsFormOpen(false);
    setSelectedCondition(null);
  };

  // Clic fuera cierra la ficha, el formulario no
  useClickOutside(isDetailKeepZone, closePanel, Boolean(selectedCondition) && !isFormOpen);

  const handleSaveCondition = async () => {
    if (saving) return;
    const name = formData.name.trim();
    if (!name) {
      setFormError('La denominación de la condición de pago es obligatoria.');
      return;
    }
    if (!(formData.days >= 0) || formData.days > 365) {
      setFormError('Los días de vencimiento deben estar entre 0 y 365.');
      return;
    }
    if (conditions.some(c => c.name.toLowerCase() === name.toLowerCase() && c.id !== selectedCondition?.id)) {
      setFormError(`Ya existe una condición de pago llamada "${name}".`);
      return;
    }
    const data: ConditionForm = { ...formData, name, days: Math.round(formData.days), description: formData.description.trim() };

    setSaving(true);
    setFormError('');
    try {
      let saved: PaymentCondition;
      if (isEditing && selectedCondition) {
        saved = await updatePaymentCondition(selectedCondition.dbId || '', data);
        notify('Éxito', `Condición de pago "${saved.name}" actualizada.`, 'info');
      } else {
        const baseId = `COND-${data.days}`;
        let id = baseId;
        for (let n = 2; conditions.some(c => c.id === id); n++) id = `${baseId}-${n}`;
        saved = await createPaymentCondition(id, data);
        notify('Éxito', `Condición de pago "${saved.name}" registrada.`, 'info');
      }
      await reload(true);
      setSelectedCondition(saved);
      setIsFormOpen(false);
    } catch (err) {
      setFormError((err as Error)?.message || 'No se pudo guardar la condición de pago.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      await deletePaymentCondition(target.dbId || '');
      notify('Éxito', `Condición de pago "${target.name}" eliminada.`, 'info');
      if (selectedCondition?.id === target.id) closePanel();
      await reload(true);
    } catch (err) {
      notify('Condiciones de pago', (err as Error)?.message || 'Error al eliminar la condición de pago.', 'warning');
    }
  };

  const query = searchQuery.toLowerCase();
  const filteredConditions = conditions.filter(c =>
    c.name.toLowerCase().includes(query) ||
    c.id.toLowerCase().includes(query) ||
    String(c.days).includes(query)
  );

  const columns: ColumnDef<PaymentCondition>[] = [
    {
      id: 'id',
      header: 'CÓDIGO',
      render: (c) => <span className="fin-master__code">{c.id}</span>
    },
    {
      id: 'name',
      header: 'DENOMINACIÓN',
      render: (c) => <div className="fin-master__name">{c.name}</div>
    },
    {
      id: 'days',
      header: 'DÍAS VENCIMIENTO',
      align: 'center',
      render: (c) => <span className="fin-master__chip">{daysLabel(c.days)}</span>
    },
    {
      id: 'description',
      header: 'DESCRIPCIÓN / CRITERIO',
      render: (c) => <span className="fin-master__desc">{c.description}</span>
    },
    {
      id: 'isActive',
      header: 'ESTADO',
      align: 'center',
      render: (c) => <span className={`module-badge ${c.isActive ? 'active' : 'inactive'}`}>{c.isActive ? 'Activa' : 'Inactiva'}</span>
    }
  ];

  return (
    <div className="fin-master">
      <div className="module-toolbar">
        <div className="module-search-wrapper">
          <Search className="module-search-icon" size={14} />
          <input
            type="text"
            placeholder="BUSCAR POR DENOMINACIÓN O DÍAS DE VENCIMIENTO..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="module-search-input"
          />
        </div>
        <button onClick={handleCreateNewClick} className="module-create-btn" data-keep-detail>
          <Plus size={14} /> Nueva_Condición
        </button>
      </div>

      <MasterDetailLayout isOpen={Boolean(selectedCondition || isFormOpen)}>
        <DataTable
          data={filteredConditions}
          columns={columns}
          title={<><CalendarClock size={14} className="module-panel-title-icon" /> Maestro_De_Condiciones_De_Pago</>}
          subtitle={`TOTAL: ${filteredConditions.length}`}
          getRowId={(c) => c.id}
          onRowClick={handleSelectCondition}
          selectedRowId={selectedCondition?.id}
          emptyText={loading ? 'Cargando condiciones de pago...' : loadError || 'No se encontraron condiciones de pago.'}
          actionsHeader="ACCIONES"
          actionsColumn={(c) => (
            <RowActionsMenu
              actions={[
                viewAction(() => handleSelectCondition(c)),
                editAction(() => handleEditClick(c)),
                deleteAction(() => setDeleteTarget(c))
              ]}
            />
          )}
        />

        {isFormOpen ? (
          <SidebarForm
            expandable
            title={isEditing ? 'Modificar_Condición_De_Pago' : 'Alta_De_Condición_De_Pago'}
            onClose={closePanel}
            submitLabel={saving ? 'Guardando...' : 'Guardar_Condición'}
            onSubmit={handleSaveCondition}
          >
            {formError && (
              <div className="module-form-error">
                <AlertCircle size={14} style={{ flexShrink: 0 }} />
                <span>{formError}</span>
              </div>
            )}

            <DetailSection title="1. Datos_De_La_Condición" icon={FileText}>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Denominación *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder="Ej. Neto 45 días"
                  className="sidebar-form-input"
                />
              </div>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Días de Vencimiento *</label>
                  <input
                    type="number"
                    required
                    min={0}
                    step={1}
                    value={formData.days}
                    onChange={(e) => setField('days', parseInt(e.target.value, 10) || 0)}
                    className="sidebar-form-input"
                  />
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Estado</label>
                  <select
                    value={formData.isActive ? 'active' : 'inactive'}
                    onChange={(e) => setField('isActive', e.target.value === 'active')}
                    className="sidebar-form-input"
                  >
                    <option value="active">ACTIVA</option>
                    <option value="inactive">INACTIVA</option>
                  </select>
                </div>
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Descripción / Criterio</label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setField('description', e.target.value)}
                  placeholder="Detalles sobre plazo legal, aplicación a librerías o distribuidores..."
                  className="sidebar-form-input"
                />
              </div>
            </DetailSection>
          </SidebarForm>
        ) : selectedCondition ? (
          <DetailPanel
            expandable
            title="Ficha_De_Condición_De_Pago"
            onClose={closePanel}
            headerActions={
              <DetailHeaderButton icon={Edit3} label="Editar" onClick={() => handleEditClick(selectedCondition)} />
            }
          >
            <DetailIdentity
              icon={CalendarClock}
              reference={selectedCondition.id}
              name={selectedCondition.name}
              subtitle={
                <span className={`module-badge ${selectedCondition.isActive ? 'active' : 'inactive'}`}>
                  {selectedCondition.isActive ? 'Activa' : 'Inactiva'}
                </span>
              }
            />

            <DetailFieldList>
              <DetailField icon={Hash} label="DÍAS_DE_VENCIMIENTO">{daysLabel(selectedCondition.days)}</DetailField>
            </DetailFieldList>

            {selectedCondition.description && (
              <DetailNotes title="Descripción / criterio">{selectedCondition.description}</DetailNotes>
            )}
          </DetailPanel>
        ) : null}
      </MasterDetailLayout>

      <Modal
        isOpen={!!deleteTarget}
        type="urgent"
        title="Eliminar condición de pago"
        message={`¿Eliminar "${deleteTarget?.name}"? Los clientes o proveedores con ese plazo conservan sus días, pero la condición dejará de estar en el maestro. Para retirarla sin borrarla, márquela como inactiva.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default FinancePaymentTermsView;
